import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/custom/button.jsx'
import { ChartLine, RefreshCcw, Search } from 'lucide-react'
import CreateSmartQc from '@/components/smartqc-route/create-qc/index.jsx'
import { Card, CardContent } from '@/components/ui/card.jsx'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@/components/ui/form.jsx'
import { Label } from '@/components/custom/label.jsx'
import { Input } from '@/components/ui/input.jsx'
import SelectQcCampaignStatus from '@/components/smartqc-route/select/qc-status.jsx'
import { format } from 'date-fns'
import SmartQcTable from '@/components/smartqc-route/qc-table/index.jsx'
import Pagination from '@/components/custom/pagination.jsx'
import { Layout } from '@/components/custom/layout.jsx'
import { useRef } from 'react'
import {
  initiateFilterState,
  useSmartQcListFilterStore,
} from '@/store/smart-qc-store/smart-qc-list-filter-store.js'
import { useShallow } from 'zustand/shallow'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import QUERY_KEY from '@/constants/use-query-key.js'
import { QcCampaignStatus, SmartQaType } from '@/constants/smart-qc.js'
import { getListSmartQc } from '@/api/smart-qc/smart-qc.js'
import RangeDatePicker from '@/components/custom/range-date-picker'

export const Route = createFileRoute('/_auth/smart-qc/autoQa/')({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const [
    filter,
    page,
    pageSize,
    lastRefreshTime,
    setPage,
    setPageSize,
    setFilter,
  ] = useSmartQcListFilterStore(
    useShallow((state) => [
      state.filter,
      state.page,
      state.pageSize,
      state.lastRefreshTime,
      state.setPage,
      state.setPageSize,
      state.setFilter,
    ]),
  )

  const formRef = useRef(null)
  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      ...filter,
    },
  })

  const { isLoading, data: smartQcsQuery } = useQuery({
    queryKey: [
      QUERY_KEY.GET_LIST_SMART_AUTO_QC,
      page,
      pageSize,
      lastRefreshTime,
    ],
    queryFn: async () => {
      const body = {
        page,
        page_size: pageSize,
        from_date: format(filter.range_date.from, 'yyyy-MM-dd'),
        to_date: format(filter.range_date.to, 'yyyy-MM-dd'),
        manual: false,
      }

      if (filter.name) {
        body.name = filter.name.trim()
      }

      if (filter.status) {
        body.status =
          filter.status === '-1'
            ? [QcCampaignStatus.WaitToProcess, QcCampaignStatus.Processing]
            : [Number(filter.status)]
      }

      const { data } = await getListSmartQc({ data: body })

      return data
    },
  })

  const smartQcs = isLoading ? [] : (smartQcsQuery?.data ?? [])
  const totalRecords = isLoading ? 0 : (smartQcsQuery?.total ?? 0)

  const onSubmit = (fieldsValue) => {
    setFilter(fieldsValue)
  }

  const refreshFilter = () => {
    form.reset(initiateFilterState)

    formRef.current.requestSubmit()
  }

  return (
    <Layout.Body className="flex w-full flex-1 flex-col gap-3 overflow-hidden">
      <div className="sticky top-0 flex items-center justify-between">
        <h1 className="text-lg font-medium">Danh sách chiến dịch auto QA</h1>
        <Button
          className="hidden"
          leftSection={<ChartLine className="h-4 w-4" />}
          onClick={() =>
            navigate({
              to: '/smart-qc/dashboard',
            })
          }
        >
          Xem báo cáo
        </Button>
        <CreateSmartQc smartQaType={SmartQaType.Auto} />
      </div>
      <Card className="h-full w-full overflow-auto">
        <CardContent className="flex h-full flex-col space-y-4 p-4">
          <div className="sticky top-0">
            <Form {...form}>
              <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 items-end gap-4 xl:grid-cols-3">
                  <div className="col-span-1 grid grid-cols-1 gap-2 lg:grid-cols-3 xl:col-span-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="col-span-1 space-y-1">
                          <Label>Tên chiến dịch</Label>
                          <FormControl>
                            <Input {...field} placeholder="Tên chiến dịch" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem className="col-span-1 space-y-1">
                          <Label>Trạng thái</Label>
                          <FormControl>
                            <SelectQcCampaignStatus {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="range_date"
                      render={({ field }) => (
                        <FormItem className="col-span-1 space-y-1">
                          <Label>Thời gian (từ - đến)</Label>
                          <div>
                            <RangeDatePicker
                              rangeDate={field.value}
                              onChangeRangeDate={field.onChange}
                            />
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end gap-2">
                    <Button
                      leftSection={<Search className="h-4 w-4" />}
                      onClick={() => {
                        formRef.current.requestSubmit()
                      }}
                    >
                      Lọc
                    </Button>
                    <Button variant="outline" onClick={() => refreshFilter()}>
                      <RefreshCcw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </div>
          <div className="overflow-auto">
            <SmartQcTable isLoading={isLoading} smartQcs={smartQcs} />
          </div>
          <div className="sticky bottom-0">
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          </div>
        </CardContent>
      </Card>
    </Layout.Body>
  )
}
